import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Container, Link2, Repeat, Sparkles, Unlink, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { describeError, useConfirmBatchAmounts, useSaveSettings, useSettings } from "@/lib/api";
import {
  byTicketId,
  centsToInputValue,
  detectRoundTripPairs,
  formatCents,
  isEmptyMove,
  isFullMove,
  parseCents,
  resolveBatchAmounts,
  type AmountSource,
  type ResolvedAmount,
} from "@/lib/amounts";
import {
  buildBatchReviewRows,
  collectAmountUpdates,
  countNeedingAmount,
  toAmountTickets,
  type BatchCaptureResult,
  type BatchReviewRow,
} from "@/lib/batch";

// Where a bulk capture lands: every ticket the driver just photographed,
// priced. Local drayage runs a flat rate, so the screen's job is to make
// the common batch require almost no typing — set the rate once, confirm
// the round trips it spotted, and the amounts are done.
//
// All money logic lives in lib/amounts.ts. This file collects three
// inputs (the rate, the pair totals, any manual edits), hands them to
// resolveBatchAmounts, and renders the answer. It never decides an
// amount itself — that is what keeps the rules testable without a DOM.
//
// Rendered by Capture rather than routed to: the batch only exists in the
// memory of the screen that uploaded it, and a route the driver could
// reload into an empty batch would be a worse lie than no route.

// A round trip the screen is tracking. Candidates arrive unconfirmed from
// detection; `confirmed` is the driver's yes. Only confirmed pairs reach
// the resolver — auto-pairing proposes, it never silently re-prices.
interface PairState {
  key: string;
  containerNo: string;
  fullId: string;
  emptyId: string;
  // What the driver typed for the pair total, as dollars text.
  totalInput: string;
  confirmed: boolean;
  // True when the driver linked these by hand rather than detection.
  manual: boolean;
}

export function BatchReview({
  results,
  onDone,
}: {
  results: BatchCaptureResult[];
  onDone: () => void;
}) {
  const rows = useMemo(() => buildBatchReviewRows(results), [results]);
  const tickets = useMemo(() => toAmountTickets(rows), [rows]);
  const rowByKey = useMemo(() => new Map(rows.map((r) => [r.key, r])), [rows]);

  const settingsQuery = useSettings();
  const confirm = useConfirmBatchAmounts();
  const saveSettings = useSaveSettings();

  const [rateInput, setRateInput] = useState("");
  const [rateTouched, setRateTouched] = useState(false);
  const [manualOverrides, setManualOverrides] = useState<Record<string, number>>({});
  const [selected, setSelected] = useState<string[]>([]);

  // Seed the pair list from detection. Unconfirmed: the driver decides.
  // Totals prefill from the FULL leg's handwritten figure, which is where
  // a driver writes the round trip's pay when they write it at all.
  const [pairs, setPairs] = useState<PairState[]>(() =>
    detectRoundTripPairs(tickets).map((p) => ({
      key: `${p.fullId}|${p.emptyId}`,
      containerNo: p.containerNo,
      fullId: p.fullId,
      emptyId: p.emptyId,
      totalInput: centsToInputValue(
        tickets.find((t) => t.id === p.fullId)?.handwrittenCents ?? null,
      ),
      confirmed: false,
      manual: false,
    })),
  );

  // Prefill the rate from the org's saved setting — once. After the driver
  // has touched the field, a late-arriving query must not overwrite it.
  const savedRateCents = settingsQuery.data?.settings?.defaultRateCents ?? null;
  useEffect(() => {
    if (rateTouched) return;
    if (savedRateCents === null) return;
    setRateInput(centsToInputValue(savedRateCents));
  }, [savedRateCents, rateTouched]);

  const defaultRateCents = parseCents(rateInput);

  const confirmedPairs = useMemo(
    () =>
      pairs
        .filter((p) => p.confirmed)
        .map((p) => ({
          fullId: p.fullId,
          emptyId: p.emptyId,
          totalCents: parseCents(p.totalInput),
        })),
    [pairs],
  );

  const resolved = useMemo(
    () => resolveBatchAmounts(tickets, { defaultRateCents, confirmedPairs, manualOverrides }),
    [tickets, defaultRateCents, confirmedPairs, manualOverrides],
  );
  const resolvedByKey = useMemo(() => byTicketId(resolved), [resolved]);

  const needAmount = countNeedingAmount(resolved);
  const captured = rows.filter((r) => !r.failed).length;
  const failed = rows.length - captured;

  // Keys spoken for by a pair CARD (confirmed or not) — those legs render
  // inside the round-trip card instead of as loose tickets.
  const pairedKeys = useMemo(
    () => new Set(pairs.flatMap((p) => [p.fullId, p.emptyId])),
    [pairs],
  );
  const looseRows = rows.filter((r) => !pairedKeys.has(r.key));

  function setOverride(key: string, value: string) {
    const cents = parseCents(value);
    setManualOverrides((s) => {
      const next = { ...s };
      // Clearing the field drops the override and hands the ticket back to
      // the rules — the driver's escape hatch from a mis-tap.
      if (cents === null) delete next[key];
      else next[key] = cents;
      return next;
    });
  }

  function clearOverride(key: string) {
    setManualOverrides((s) => {
      const next = { ...s };
      delete next[key];
      return next;
    });
  }

  function toggleSelected(key: string) {
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key].slice(-2)));
  }

  // Manual link. Restricted to one loaded + one empty leg because that is
  // the only shape the split arithmetic is defined for — linking two
  // loaded legs would have nothing to give the empty rate.
  const linkable = useMemo(() => {
    if (selected.length !== 2) return null;
    const [a, b] = selected.map((k) => rowByKey.get(k));
    if (!a || !b) return null;
    const full = isFullMove(a.moveType) ? a : isFullMove(b.moveType) ? b : null;
    const empty = isEmptyMove(a.moveType) ? a : isEmptyMove(b.moveType) ? b : null;
    if (!full || !empty || full.key === empty.key) return null;
    return { full, empty };
  }, [selected, rowByKey]);

  function linkSelected() {
    if (!linkable) return;
    const { full, empty } = linkable;
    setPairs((s) => [
      ...s,
      {
        key: `${full.key}|${empty.key}`,
        containerNo: full.containerNo ?? empty.containerNo ?? "—",
        fullId: full.key,
        emptyId: empty.key,
        totalInput: centsToInputValue(full.handwrittenCents),
        // Linked by hand IS the confirmation — the driver just said so.
        confirmed: true,
        manual: true,
      },
    ]);
    setSelected([]);
  }

  function unlink(key: string) {
    setPairs((s) => s.filter((p) => p.key !== key));
  }

  function confirmPair(key: string) {
    setPairs((s) => s.map((p) => (p.key === key ? { ...p, confirmed: true } : p)));
  }

  function setPairTotal(key: string, value: string) {
    setPairs((s) => s.map((p) => (p.key === key ? { ...p, totalInput: value } : p)));
  }

  function onConfirmAll() {
    const updates = collectAmountUpdates(rows, resolved);
    confirm.mutate(updates, {
      onSuccess: () => {
        // Remember the rate for next time. Fire-and-forget on purpose: the
        // amounts are already saved, and failing to persist a preference
        // must not present as a failed batch.
        if (defaultRateCents !== null && defaultRateCents !== savedRateCents) {
          saveSettings.mutate({ defaultRateCents });
        }
        onDone();
      },
    });
  }

  return (
    <div className="space-y-4" data-testid="batch-review">
      <div>
        <h2 className="text-lg font-semibold">Check your pay</h2>
        <p className="text-sm text-muted-foreground">
          {captured} ticket{captured === 1 ? "" : "s"} read
          {failed > 0 && `, ${failed} failed`}
          {needAmount > 0 && (
            <span className="text-amber-600 dark:text-amber-500">
              {" "}
              · {needAmount} need{needAmount === 1 ? "s" : ""} an amount
            </span>
          )}
        </p>
      </div>

      <Card className="p-3">
        <label htmlFor="default-rate" className="text-sm font-medium">
          Default rate
        </label>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Fills every ticket with no handwritten amount, and pays the empty leg of a round trip.
        </p>
        <div className="relative mt-2 w-36">
          <span
            aria-hidden="true"
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          >
            $
          </span>
          <Input
            id="default-rate"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            placeholder="0.00"
            aria-label="Default rate"
            data-testid="default-rate"
            className="h-11 pl-6 tabular-nums"
            value={rateInput}
            onChange={(e) => {
              setRateTouched(true);
              setRateInput(e.target.value);
            }}
          />
        </div>
      </Card>

      {pairs.length > 0 && (
        <ul className="space-y-2" data-testid="round-trips">
          {pairs.map((pair) => (
            <RoundTripCard
              key={pair.key}
              pair={pair}
              full={rowByKey.get(pair.fullId)}
              empty={rowByKey.get(pair.emptyId)}
              resolvedFull={resolvedByKey[pair.fullId]}
              resolvedEmpty={resolvedByKey[pair.emptyId]}
              onTotalChange={(v) => setPairTotal(pair.key, v)}
              onConfirm={() => confirmPair(pair.key)}
              onUnlink={() => unlink(pair.key)}
            />
          ))}
        </ul>
      )}

      <ul className="space-y-2">
        {looseRows.map((row) => (
          <BatchRow
            key={row.key}
            row={row}
            resolved={resolvedByKey[row.key]}
            selected={selected.includes(row.key)}
            onToggleSelected={() => toggleSelected(row.key)}
            onChange={(v) => setOverride(row.key, v)}
            onClearOverride={() => clearOverride(row.key)}
          />
        ))}
      </ul>

      {selected.length === 2 && (
        <Card className="p-3 flex items-center justify-between gap-3" data-testid="link-bar">
          <span className="text-sm text-muted-foreground">
            {linkable
              ? "Link these two as a round trip?"
              : "A round trip needs one loaded leg and one empty leg."}
          </span>
          <Button size="sm" onClick={linkSelected} disabled={!linkable} data-testid="link-pair">
            <Link2 className="h-4 w-4" /> Link
          </Button>
        </Card>
      )}

      {confirm.isError && (
        <Card className="p-4 text-sm text-destructive" role="alert">
          Couldn&apos;t save the amounts: {describeError(confirm.error)}
        </Card>
      )}

      <Button
        size="lg"
        className="w-full h-14 text-base"
        onClick={onConfirmAll}
        disabled={confirm.isPending}
      >
        {confirm.isPending ? "Saving…" : "Confirm All"}
      </Button>
    </div>
  );
}

// Badge copy per rule. The driver does not care about our enum names —
// they care whether the number came off the ticket, off the rate, out of
// a split, or out of their own thumb.
const SOURCE_LABEL: Record<AmountSource, string> = {
  handwritten: "auto",
  default: "default",
  "split-full": "split",
  "split-empty": "split",
  manual: "edited",
};

function SourceBadge({ source }: { source: AmountSource }) {
  const label = SOURCE_LABEL[source];
  return (
    <span
      data-testid={`source-${source}`}
      className={
        "inline-flex items-center gap-0.5 " +
        (source === "handwritten" ? "text-primary" : "text-muted-foreground")
      }
    >
      {source === "handwritten" && <Sparkles className="h-3 w-3" />}
      {label}
    </span>
  );
}

function RoundTripCard({
  pair,
  full,
  empty,
  resolvedFull,
  resolvedEmpty,
  onTotalChange,
  onConfirm,
  onUnlink,
}: {
  pair: PairState;
  full: BatchReviewRow | undefined;
  empty: BatchReviewRow | undefined;
  resolvedFull: ResolvedAmount | undefined;
  resolvedEmpty: ResolvedAmount | undefined;
  onTotalChange: (value: string) => void;
  onConfirm: () => void;
  onUnlink: () => void;
}) {
  if (!full || !empty) return null;
  const warn = resolvedFull?.warn ?? resolvedEmpty?.warn;

  return (
    <li>
      <Card
        className={
          "p-3 space-y-2" +
          (warn
            ? " border-amber-400/80 dark:border-amber-500/60 bg-amber-50/50 dark:bg-amber-950/20"
            : pair.confirmed
              ? " border-primary/50"
              : "")
        }
        data-testid={`round-trip-${pair.key}`}
        data-confirmed={pair.confirmed ? "true" : undefined}
      >
        <div className="flex items-center gap-2 font-medium">
          <Repeat className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="truncate text-sm">
            Round trip · <span className="font-mono">{pair.containerNo}</span>
          </span>
        </div>

        <div className="text-xs text-muted-foreground">
          {full.metaLabel} + {empty.metaLabel}
        </div>

        <div className="flex items-center gap-3">
          <div className="relative w-32 shrink-0">
            <span
              aria-hidden="true"
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              $
            </span>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder="0.00"
              aria-label={`Total for round trip ${pair.containerNo}`}
              data-testid={`pair-total-${pair.key}`}
              className="h-11 pl-6 tabular-nums"
              value={pair.totalInput}
              onChange={(e) => onTotalChange(e.target.value)}
            />
          </div>
          <div className="min-w-0 text-sm tabular-nums" data-testid={`pair-preview-${pair.key}`}>
            {pair.confirmed && resolvedFull && resolvedEmpty ? (
              <>
                Full {formatCents(resolvedFull.amountCents) ?? "—"} ·{" "}
                Empty {formatCents(resolvedEmpty.amountCents) ?? "—"}
              </>
            ) : (
              <span className="text-muted-foreground">Total for both legs</span>
            )}
          </div>
        </div>

        {warn && (
          <p role="alert" className="text-xs text-amber-700 dark:text-amber-500">
            {warn}
          </p>
        )}

        <div className="flex gap-2">
          {!pair.confirmed && (
            <Button size="sm" onClick={onConfirm} data-testid={`confirm-pair-${pair.key}`}>
              Confirm
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={onUnlink}
            data-testid={`unlink-pair-${pair.key}`}
          >
            <Unlink className="h-4 w-4" /> Unlink
          </Button>
        </div>
      </Card>
    </li>
  );
}

function BatchRow({
  row,
  resolved,
  selected,
  onToggleSelected,
  onChange,
  onClearOverride,
}: {
  row: BatchReviewRow;
  resolved: ResolvedAmount | undefined;
  selected: boolean;
  onToggleSelected: () => void;
  onChange: (value: string) => void;
  onClearOverride: () => void;
}) {
  // Flag on the RESOLVED amount: a rate that prices this row clears its
  // highlight without the driver touching it.
  const flagged = !row.failed && (resolved?.amountCents ?? null) === null;
  const value = centsToInputValue(resolved?.amountCents ?? null);

  return (
    <li>
      <Card
        className={
          "p-3 flex items-center gap-3" +
          (row.failed
            ? " border-destructive/60"
            : flagged
              ? " border-amber-400/80 dark:border-amber-500/60 bg-amber-50/50 dark:bg-amber-950/20"
              : selected
                ? " border-primary"
                : "")
        }
        data-testid={`batch-row-${row.key}`}
        data-flagged={flagged ? "true" : undefined}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 font-medium">
            {row.failed ? (
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
            ) : (
              <Container className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            <span className="truncate font-mono text-sm">{row.containerLabel}</span>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {row.failed ? (
              <span className="text-destructive">{row.errorMessage} — re-shoot this one</span>
            ) : (
              <span className="flex items-center gap-1.5">
                {row.metaLabel}
                {resolved && <SourceBadge source={resolved.source} />}
                {resolved?.source === "manual" && (
                  <button
                    type="button"
                    onClick={onClearOverride}
                    aria-label={`Undo edit for ${row.containerLabel}`}
                    data-testid={`clear-override-${row.key}`}
                    className="inline-flex items-center hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            )}
          </div>
        </div>

        {!row.failed && (
          <>
            <button
              type="button"
              onClick={onToggleSelected}
              aria-label={`Select ${row.containerLabel} to link`}
              aria-pressed={selected}
              data-testid={`select-${row.key}`}
              className={
                "shrink-0 rounded-md border p-2 " +
                (selected ? "border-primary text-primary" : "text-muted-foreground")
              }
            >
              <Link2 className="h-4 w-4" />
            </button>
            <div className="relative shrink-0 w-28">
              <span
                aria-hidden="true"
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                $
              </span>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                placeholder="0.00"
                aria-label={`Amount for ${row.containerLabel}`}
                data-testid={`amount-${row.key}`}
                className="h-11 pl-6 tabular-nums"
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            </div>
          </>
        )}
      </Card>
    </li>
  );
}
